import clsx from "clsx"
import { CSSProperties, FC, MouseEventHandler, TouchEventHandler, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import style from "./Tooltip.module.css"

export type TooltipPosition = "tooltip-top" | "tooltip-right" | "tooltip-bottom" | "tooltip-left"

const PORTAL_GAP = 8

const portalPosition = (rect: DOMRect, position: TooltipPosition): CSSProperties => {
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  switch (position) {
    case "tooltip-top":
      return { top: rect.top - PORTAL_GAP, left: centerX, transform: "translate(-50%, -100%)" }
    case "tooltip-bottom":
      return { top: rect.bottom + PORTAL_GAP, left: centerX, transform: "translate(-50%, 0)" }
    case "tooltip-left":
      return { top: centerY, left: rect.left - PORTAL_GAP, transform: "translate(-100%, -50%)" }
    case "tooltip-right":
      return { top: centerY, left: rect.right + PORTAL_GAP, transform: "translate(0, -50%)" }
  }
}

type TooltipProps = {
  children: JSX.Element | JSX.Element[]
  tooltipPosition: TooltipPosition
  title: string
  width?: "fit" | "full"
  className?: string
  display?: boolean
  tooltipUseMinWidth?: boolean
  delay?: number
  portal?: boolean
}

export const Tooltip: FC<TooltipProps> = ({
  children,
  tooltipPosition,
  title,
  width = "full",
  className,
  display = true,
  tooltipUseMinWidth = true,
  delay = 0,
  portal = false,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [portalStyle, setPortalStyle] = useState<CSSProperties | null>(null)
  const parentRef = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(timer.current), [])

  const reveal = () => {
    if (portal && parentRef.current) {
      setPortalStyle(portalPosition(parentRef.current.getBoundingClientRect(), tooltipPosition))
    }
    setIsVisible(true)
  }

  const show = () => {
    clearTimeout(timer.current)
    if (delay > 0) {
      timer.current = setTimeout(reveal, delay)
    } else {
      reveal()
    }
  }

  const hide = () => {
    clearTimeout(timer.current)
    setIsVisible(false)
  }

  if (!display) {
    return <>{children}</>
  }

  const handleMouseEnter: MouseEventHandler<HTMLDivElement> = () => show()
  const handleMouseLeave: MouseEventHandler<HTMLDivElement> = () => hide()
  const handleTouchStart: TouchEventHandler<HTMLDivElement> = () => show()
  const handleTouchEnd: TouchEventHandler<HTMLDivElement> = () => hide()

  const bubble = (
    <TooltipItem
      title={title}
      tooltipPosition={tooltipPosition}
      tooltipUseMinWidth={tooltipUseMinWidth}
      fixedStyle={portal && portalStyle ? { ...portalStyle, zIndex: 70 } : undefined}
    />
  )

  return (
    <div
      ref={parentRef}
      className={clsx(
        className,
        style["tooltip-parent"],
        width === "fit" ? style["tooltip-fit"] : style["tooltip-full"]
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={show}
      onBlur={hide}
      onPointerDown={hide}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {children}
      {isVisible && (portal ? portalStyle && createPortal(bubble, document.body) : bubble)}
    </div>
  )
}

type TooltipItemProps = {
  title: string
  tooltipPosition: TooltipPosition
  tooltipUseMinWidth: boolean
  // When set (portal mode), position via inline `position: fixed` instead of the wrapper-relative class.
  fixedStyle?: CSSProperties
}

const TooltipItem: FC<TooltipItemProps> = ({ title, tooltipPosition, tooltipUseMinWidth, fixedStyle }) => {
  return (
    <div
      role={"tooltip"}
      className={clsx(
        style["tooltip-container"],
        !fixedStyle && style[tooltipPosition],
        tooltipUseMinWidth && style["tooltip-use-min-width"]
      )}
      style={fixedStyle ? { position: "fixed", ...fixedStyle } : undefined}
    >
      <p className={style["tooltip-title"]}>{title}</p>
    </div>
  )
}
